import styled from 'styled-components';
import {
  Box,
  Flex,
  Checkbox,
  mainTheme,
  Image,
  Icon,
  ExpandableText,
  Button,
  ModalContext,
} from 'dash-ui';

import {
  Amenity,
  GetAmenitiesDocument,
  GetAmenitiesForManageDocument,
  useDeleteAmenityMutation,
  useDuplicateAmenityMutation,
} from 'gql/generated.graphql';
import { Dispatch, SetStateAction, useContext } from 'react';

const AmenityImage = styled(Image)`
  border: 1px solid ${mainTheme.colors.n30};
`;

const AmenityFlexWrapper = styled(Flex)`
  height: 80px;

  &:not(:last-child) {
    border-bottom: 1px solid ${mainTheme.colors.n30};
  }
`;

const StyledIcon = styled(Icon)`
  &:hover {
    cursor: pointer;
  }
`;

interface IProps {
  amenity: Amenity;
  isCheckboxChecked: boolean;
  onCheckboxChange: (checked: boolean) => void;
  onEditIconClick: () => void;
  disabled: boolean;
  setSelectedAmenitiesIds: Dispatch<SetStateAction<string[]>>;
}

function AmenityListItem({
  amenity,
  isCheckboxChecked,
  onCheckboxChange,
  onEditIconClick,
  disabled,
  setSelectedAmenitiesIds,
}: IProps) {
  const [duplicateAmenity] = useDuplicateAmenityMutation();
  const [deleteAmenity] = useDeleteAmenityMutation();
  const { payload } = useContext(ModalContext);

  const handleDuplicateClick = async () => {
    const { data } = await duplicateAmenity({
      variables: {
        amenityId: amenity._id,
        propertyId: payload.property._id,
      },
      refetchQueries: [GetAmenitiesForManageDocument],
    });
    if (data?.duplicateAmenity._id)
      setSelectedAmenitiesIds((prevState) => [
        ...prevState,
        data?.duplicateAmenity._id,
      ]);
  };

  const handleRemoveClick = () => {
    deleteAmenity({
      variables: {
        amenityId: amenity._id,
      },
      refetchQueries: [GetAmenitiesForManageDocument, GetAmenitiesDocument],
    });
  };

  return (
    <AmenityFlexWrapper
      data-testid="amenity-list"
      w="100%"
      flexDirection="row"
      justifyContent="space-between"
      alignItems="center"
    >
      <Flex flexDirection="row" alignItems="center" w="100%">
        <Checkbox
          checked={isCheckboxChecked}
          onChange={onCheckboxChange}
          mr={3}
          disabled={disabled}
        />
        <Box mr={4}>
          <AmenityImage
            fit="scale-down"
            size={56}
            src={amenity.imageUrl || ''}
            alt={amenity.name}
          />
        </Box>
        <ExpandableText
          withButton={false}
          lineClamp={3}
          color={mainTheme.colors.n80}
        >
          {amenity.name}
        </ExpandableText>
        <Box>
          <StyledIcon
            ml={3}
            name="edit"
            color={mainTheme.colors.b50}
            onClick={onEditIconClick}
          />
        </Box>
      </Flex>
      {amenity.isPrimary ? (
        <Button
          disabled={!isCheckboxChecked}
          variation="flat"
          onClick={handleDuplicateClick}
        >
          Duplicate
        </Button>
      ) : (
        <Button variation="flat" onClick={handleRemoveClick}>
          Remove
        </Button>
      )}
    </AmenityFlexWrapper>
  );
}

export default AmenityListItem;
